import axiosService from "./axios.service";
import { Branding } from "./branding.service";

export interface OrganizationSummary {
    _id: string;
    name: string;
    slug: string;
    branding: Branding;
    // what new hackathons in the org start with: an email domain to limit joining to, and a suggested sign-in
    access?: { domain: string, preferredAuth: string };
    isAdmin?: boolean;
}

export interface OrganizationDetail extends OrganizationSummary {
    domains: string[];
    plan: string;
    admins: Array<{ _id: string, username: string, email: string }>;
    createdAt?: string;
}

export const getMyOrganizations = async (): Promise<OrganizationSummary[]> => {
    const { data } = await axiosService.get('/api/organizations/mine');
    return data;
}

export const getOrganization = async (id: string): Promise<OrganizationDetail> => {
    const { data } = await axiosService.get(`/api/organizations/${id}`);
    return data;
}

export const updateOrganization = async (id: string, options: { name?: string, branding?: Branding, domains?: string[], plan?: string, access?: { domain: string, preferredAuth: string } }): Promise<OrganizationDetail> => {
    const { data } = await axiosService.put(`/api/organizations/${id}`, options);
    return data;
}

/** Org admins (and platform admins): deletes the organization; its hackathons stay, unlinked. */
export const deleteMyOrganization = async (id: string) => {
    const { data } = await axiosService.delete(`/api/organizations/${id}`);
    return data;
}

export interface OrgPastHackathon {
    _id: string, name: string, slug: string, startDate: string | null, endDate: string | null, description: string,
    categories: Array<{ name: string, judgeOnly: boolean }>, ideaCount: number, participantCount: number,
    winners: Array<{ category: string, title: string }>
}
export interface OrgHistory { organization: OrganizationSummary, hackathons: OrgPastHackathon[] }

/** Past hackathons of the organizations you've been part of, that you weren't in (for discoverability). */
export const getOrganizationHistory = async (): Promise<OrgHistory[]> => {
    const { data } = await axiosService.get('/api/organizations/history');
    return data;
}

export const addOrganizationAdmin = async (id: string, email: string): Promise<OrganizationDetail> => {
    const { data } = await axiosService.post(`/api/organizations/${id}/addadmin`, { email });
    return data;
}

export const removeOrganizationAdmin = async (id: string, email: string): Promise<OrganizationDetail> => {
    const { data } = await axiosService.post(`/api/organizations/${id}/removeadmin`, { email });
    return data;
}

export interface OrganizationEvent { _id: string, name: string, slug: string, plan: 'free' | 'pro' }

// org admins: the company's hackathons and their plans
export const getOrganizationEvents = async (id: string): Promise<OrganizationEvent[]> => {
    const { data } = await axiosService.get(`/api/organizations/${id}/hackathons`);
    return data;
}

// self-serve: the signed-in user sets up a company and becomes its admin
export const createMyOrganization = async (name: string): Promise<OrganizationDetail> => {
    const { data } = await axiosService.post('/api/organizations', { name });
    return data;
}

// platform admin only
export const listAllOrganizations = async (): Promise<OrganizationDetail[]> => {
    const { data } = await axiosService.get('/api/admin/organizations');
    return data;
}

export const createOrganization = async (options: { name: string, domains: string[], adminEmail?: string }): Promise<OrganizationDetail> => {
    const { data } = await axiosService.post('/api/admin/organizations', options);
    return data;
}

export const deleteOrganization = async (id: string) => {
    const { data } = await axiosService.delete(`/api/admin/organizations/${id}`);
    return data;
}
